import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * КРУГИ НА ВОДЕ — эффект подложки с рисовыми террасами (rice_1).
 *
 * Поле этой подложки лежит на большом залитом пруду. То тут, то там по воде
 * расходятся круги — будто плеснула рыба или упала капля: два-три кольца одно
 * за другим растут из точки и тают (см. utils/effects/biome/rippleRings).
 *
 * Вода пруда отражает небо: вверху картинки она почти белая, внизу — темная
 * оливковая. Белое кольцо не видно на светлой воде, темное — на темной,
 * поэтому каждое кольцо — пара: светлый гребень и темная ложбина чуть крупнее
 * и чуть ниже-правее. Какая-то из двух видна на любой воде.
 *
 * Чего здесь нельзя:
 *  - кольцо крупнее ячейки: растущий круг — привычный сигнал интерфейса, и
 *    круг, выросший из-под жетона, читался бы меткой;
 *  - частые круги: пруд превратился бы в дождь;
 *  - круги на террасах: углы поля лежат на дамбах, кольцо поверх них — ошибка.
 */
export const RIPPLE_RINGS_KIND = 'rippleRings';

export const RIPPLE_RINGS_EFFECT = Object.freeze({
  kind: RIPPLE_RINGS_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.RICE_1]: Object.freeze({
      firstDelayMs: {min: 300, max: 1200},
      // Всплеск — одно место на воде; пауза — от начала предыдущего. Когда в
      // кадре весь пруд, всплесков два-три в секунду, а живет их разом около
      // восьми: реже — на телефоне их не заметить, чаще — пруд пойдет дождем
      intervalMs: {min: 280, max: 560},
      // Больше стольких всплесков разом не живет
      maxGroups: 12,
      // Пруд не начинается с гладкой воды: первые всплески уже расходятся
      prewarm: 4,
      // Темп всплесков — по числу ячеек пруда в кадре: при стольких ячейках
      // (весь пруд обычного поля) он полный, при меньшем числе — во столько же
      // раз реже. Иначе при приближении камеры все всплески собирались бы на
      // видимом клочке воды. Реже этой доли темп не падает
      fullRateCells: 160,
      minRateShare: 0.12,
      splash: Object.freeze({
        // Колец во всплеске; с такой вероятностью — на одно больше
        ringCount: 2,
        extraRingChance: 0.3,
        // Каждое следующее кольцо рождается на столько позже предыдущего
        ringStaggerMs: 450,
        // Место всплеска — где угодно в пределах ячейки, но не ближе стольких
        // ее размеров к центру: кольцо вокруг центра ячейки читалось бы меткой
        minCellOffsetFactor: 0.3,
      }),
      ring: Object.freeze({
        lifeMs: {min: 2600, max: 3400},
        // Радиус кольца: от точки до почти края ячейки, размеров ячейки (от
        // центра до угла). Больше нельзя — кольцо должно умещаться под жетоном
        startRadiusFactor: 0.12,
        endRadiusFactor: {min: 0.8, max: 0.88},
        // Проступает за эту долю жизни и тает к концу: (1 − доля жизни) в
        // этой степени. Кольцо растет — его полоса толстеет, а яркость падает
        fadeInShare: 0.12,
        fadePower: 1,
        // Светлый гребень: виден на темной воде. Полоса кольца на телефоне —
        // тоньше точки экрана, и слабее этого ее не разглядеть
        crest: Object.freeze({tint: 0xfbfcf4, alpha: 0.66}),
        // Темная ложбина: видна на светлой воде. Крупнее гребня во столько раз
        // и сдвинута вправо-вниз (размеров ячейки) — выглядывает из-под него
        trough: Object.freeze({
          tint: 0x3f4a34, alpha: 0.55, scale: 1.07, shiftFactor: Object.freeze({x: 0.024, y: 0.032}),
        }),
      }),
      // Вода пруда на картинке пейзажа (доли ее стороны): круг, сверенный с
      // картинкой, — в нем всюду вода. Шире нельзя: уже при 0.265 он задевает
      // террасы слева вверху и справа внизу. Выросшее кольцо из этого круга не
      // выходит: середины всплесков держатся от его кромки на размер кольца
      pond: Object.freeze({u: 0.5, v: 0.5, radiusU: 0.24}),
    }),
  }),
});
